import {
  contentTypeOfFileName,
  isUploadContentType,
  MAX_CREATIVES_PER_BATCH,
  type UploadContentType,
  validateUpload,
} from '@alison/api/uploads';
import type { WorkflowField } from './workflows.types.js';

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;
const BYTE_STEP = 1024;

export type UploadStatus = 'pending' | 'uploading' | 'done' | 'failed';

export interface UploadRow {
  key: string;
  file: File;
  fileName: string;
  sizeBytes: number;
  contentType: UploadContentType;
  status: UploadStatus;
  error: string | null;
  objectName: string | null;
}

export interface RejectedFile {
  fileName: string;
  reason: string;
}

export interface AddedFiles {
  rows: UploadRow[];
  rejected: RejectedFile[];
}

export interface SettledUpload {
  objectName?: string;
  error?: string;
}

function contentTypeOf(file: File): string {
  if (isUploadContentType(file.type)) return file.type;
  return contentTypeOfFileName(file.name) ?? (file.type === '' ? 'unknown' : file.type);
}

function rowOf(file: File, contentType: UploadContentType): UploadRow {
  return {
    key: crypto.randomUUID(),
    file,
    fileName: file.name,
    sizeBytes: file.size,
    contentType,
    status: 'pending',
    error: null,
    objectName: null,
  };
}

export function addFiles(existing: UploadRow[], files: File[]): AddedFiles {
  const rows = [...existing];
  const rejected: RejectedFile[] = [];

  for (const file of files) {
    if (rows.length >= MAX_CREATIVES_PER_BATCH) {
      rejected.push({
        fileName: file.name,
        reason: `a batch holds ${MAX_CREATIVES_PER_BATCH} creatives`,
      });
      continue;
    }
    const check = validateUpload({ contentType: contentTypeOf(file), sizeBytes: file.size });
    if (check.ok) {
      rows.push(rowOf(file, check.contentType));
    } else {
      rejected.push({ fileName: file.name, reason: check.reason });
    }
  }

  return { rows, rejected };
}

export function settleRow(rows: UploadRow[], key: string, outcome: SettledUpload): UploadRow[] {
  return rows.map((row) => {
    if (row.key !== key) return row;
    if (outcome.objectName !== undefined) {
      return { ...row, status: 'done', error: null, objectName: outcome.objectName };
    }
    if (outcome.error !== undefined) {
      return { ...row, status: 'failed', error: outcome.error };
    }
    return row;
  });
}

export function startRow(rows: UploadRow[], key: string): UploadRow[] {
  return rows.map((row) => (row.key === key ? { ...row, status: 'uploading', error: null } : row));
}

export function renameRow(rows: UploadRow[], key: string, fileName: string): UploadRow[] {
  return rows.map((row) => (row.key === key ? { ...row, fileName } : row));
}

export function removeRow(rows: UploadRow[], key: string): UploadRow[] {
  return rows.filter((row) => row.key !== key);
}

export function formatBytes(sizeBytes: number): string {
  let size = sizeBytes;
  let unit = 0;
  while (size >= BYTE_STEP && unit < BYTE_UNITS.length - 1) {
    size /= BYTE_STEP;
    unit += 1;
  }
  return `${Number(size.toFixed(1))} ${BYTE_UNITS[unit]}`;
}

export function rowLine(row: UploadRow): string {
  const kind = row.contentType === 'video/mp4' ? 'Video' : 'Image';
  return row.error ?? `${kind} · ${formatBytes(row.sizeBytes)}`;
}

export function videoCount(rows: UploadRow[]): number {
  return rows.filter((row) => row.contentType === 'video/mp4').length;
}

export function uploadedLine(total: number, videos: number): string {
  const images = total - videos;
  const parts = [
    videos === 0 ? null : `${videos} video${videos === 1 ? '' : 's'}`,
    images === 0 ? null : `${images} image${images === 1 ? '' : 's'}`,
  ].filter((part) => part !== null);
  const counted = `${total} creative${total === 1 ? '' : 's'} uploaded`;
  return parts.length === 0 ? counted : `${counted} · ${parts.join(', ')}`;
}

export function hasUploadField(fields: WorkflowField[]): boolean {
  return fields.some((field) =>
    field.kind === 'collapsible' ? hasUploadField(field.fields) : field.kind === 'upload',
  );
}

// The field holds the names of the creatives that landed: that is what the
// session map and the run summary show, and a rename shows up with it.
export function uploadedValues(
  fields: WorkflowField[],
  rows: UploadRow[],
): Record<string, string[]> {
  const landed = rows.flatMap((row) => (row.status === 'done' ? [row.fileName] : []));
  return Object.fromEntries(
    fields.flatMap((field) =>
      field.kind === 'collapsible'
        ? Object.entries(uploadedValues(field.fields, rows))
        : field.kind === 'upload'
          ? [[field.key, landed]]
          : [],
    ),
  );
}
